import {
  UserManager,
  WebStorageStateStore,
  type UserManagerSettings,
  type User,
} from 'oidc-client';
import { ApplicationPaths, ApplicationName } from './ApiAuthorizationConstants';

export enum AuthenticationResultStatus {
  Redirect = 'redirect',
  Success = 'success',
  Fail = 'fail',
}

type AuthState = unknown;

export interface AuthResult {
  status: AuthenticationResultStatus;
  message?: string;
  state?: AuthState;
}

type Subscriber = { callback: () => void; subscription: number };

type NavigationArgs = {
  useReplaceToNavigate: boolean;
  data?: AuthState;
};

export class AuthorizeService {
  private _callbacks: Subscriber[] = [];
  private _nextSubscriptionId = 0;
  private _user: User | null = null;
  private _isAuthenticated = false;

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  private _popUpDisabled = true;

  private userManager?: UserManager;

  public async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!user;
  }

  public async getUser(): Promise<Record<string, unknown> | undefined> {
    if (this._user && this._user.profile) {
      return this._user.profile as Record<string, unknown>;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    return user?.profile as Record<string, unknown> | undefined;
  }

  public async getAccessToken(): Promise<string | undefined> {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    return user?.access_token;
  }

  // Authentication flow: silent -> popup -> redirect
  public async signIn(state?: AuthState): Promise<AuthResult> {
    await this.ensureUserManagerInitialized();
    try {
      const silentUser = await this.userManager!.signinSilent(
        this.createArguments(),
      );
      this.updateState(silentUser);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      // eslint-disable-next-line no-console
      console.log('Silent authentication error: ', silentError);

      try {
        if (this._popUpDisabled) {
          throw new Error(
            "Popup disabled. Change 'AuthorizeService._popUpDisabled' to false to enable it.",
          );
        }

        const popUpUser = await this.userManager!.signinPopup(
          this.createArguments(),
        );
        this.updateState(popUpUser);
        return this.success(state);
      } catch (popUpError: unknown) {
        const message =
          popUpError instanceof Error ? popUpError.message : String(popUpError);
        if (message === 'Popup window closed') {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error('The user closed the window.');
        } else if (!this._popUpDisabled) {
          // eslint-disable-next-line no-console
          console.log('Popup authentication error: ', popUpError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager!.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError) {
          // eslint-disable-next-line no-console
          console.log('Redirect authentication error: ', redirectError);
          return this.error(
            redirectError instanceof Error
              ? redirectError.message
              : String(redirectError),
          );
        }
      }
    }
  }

  public async completeSignIn(url: string): Promise<AuthResult> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager!.signinCallback(url);
      this.updateState(user);
      return this.success(user?.state as AuthState | undefined);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('There was an error signing in: ', error);
      return this.error('There was an error signing in.');
    }
  }

  // Sign out: popup -> redirect
  public async signOut(state?: AuthState): Promise<AuthResult> {
    await this.ensureUserManagerInitialized();
    try {
      if (this._popUpDisabled) {
        throw new Error(
          "Popup disabled. Change 'AuthorizeService._popUpDisabled' to false to enable it.",
        );
      }

      await this.userManager!.signoutPopup(this.createArguments());
      this.updateState(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      // eslint-disable-next-line no-console
      console.log('Popup signout error: ', popupSignOutError);
      try {
        await this.userManager!.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError) {
        // eslint-disable-next-line no-console
        console.log('Redirect signout error: ', redirectSignOutError);
        return this.error(
          redirectSignOutError instanceof Error
            ? redirectSignOutError.message
            : String(redirectSignOutError),
        );
      }
    }
  }

  public async completeSignOut(url: string): Promise<AuthResult> {
    await this.ensureUserManagerInitialized();
    try {
      const response = await this.userManager!.signoutCallback(url);
      this.updateState(null);
      return this.success((response as unknown as { data?: AuthState })?.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error instanceof Error ? error.message : String(error));
    }
  }

  private updateState(user: User | null | undefined): void {
    this._user = user ?? null;
    this._isAuthenticated = !!this._user;
    this.notifySubscribers();
  }

  public subscribe(callback: () => void): number {
    this._callbacks.push({
      callback,
      subscription: this._nextSubscriptionId++,
    });
    return this._nextSubscriptionId - 1;
  }

  public unsubscribe(subscriptionId: number): void {
    const subscriptionIndex = this._callbacks
      .map((element, index) =>
        element.subscription === subscriptionId
          ? { found: true, index }
          : { found: false, index: -1 },
      )
      .filter((element) => element.found === true);

    if (subscriptionIndex.length !== 1) {
      throw new Error(
        `Found an invalid number of subscriptions ${subscriptionIndex.length}`,
      );
    }

    this._callbacks.splice(subscriptionIndex[0].index, 1);
  }

  private notifySubscribers(): void {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  private createArguments(state?: AuthState): NavigationArgs {
    return { useReplaceToNavigate: true, data: state };
  }

  private error(message: string): AuthResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  private success(state?: AuthState): AuthResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  private redirect(): AuthResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  private async ensureUserManagerInitialized(): Promise<void> {
    if (this.userManager !== undefined) {
      return;
    }

    const response = await fetch(
      ApplicationPaths.ApiAuthorizationClientConfigurationUrl,
    );
    if (!response.ok) {
      throw new Error(`Could not load settings for '${ApplicationName}'`);
    }

    const settings = (await response.json()) as UserManagerSettings;
    // Runtime tweaks:
    (settings as any).automaticSilentRenew = true;
    (settings as any).includeIdTokenInSilentRenew = true;
    (settings as any).userStore = new WebStorageStateStore({
      prefix: ApplicationName,
    });

    this.userManager = new UserManager(settings);

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager!.removeUser();
      this.updateState(undefined);
    });
  }

  public static get instance(): AuthorizeService {
    return authService;
  }
}

const authService = new AuthorizeService();
export default authService;
